// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';

import DirectionSelect from 'components/atoms/DirectionSelect';
import Icon from 'components/atoms/Icon';
import RangeSlider from 'components/atoms/RangeSlider';
import SearchGroup from 'components/atoms/SearchGroup';
import ToggleButton from 'components/atoms/ToggleButton';
import { Tooltip } from 'react-tooltip';
import { SetSearchObjectAction } from 'store/actions/layoutActions';
import { EnviroDisplayOrderType, EnviroDisplayType, PortType, PortURLType, RootState } from 'types';
import { DefaultData, FilterObjectType } from 'types/search';
import { SystemType } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone, getKey, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twMerge } from 'tailwind-merge';

interface MetOceanFiltersComponentProps {
    port: PortType;
    filterObject: FilterObjectType;
    defaultData: DefaultData;
    dateTimeFormat: DateTimeFormat;
    setFilterObject: SetSearchObjectAction;
    system: SystemType;
}

interface MetOceanFiltersComponentState {
    port: PortType | null;
    enviroDisplayOrder: EnviroDisplayOrderType[] | undefined;
    environmentalSources: PortURLType[];
    enviroDisplay: EnviroDisplayType | undefined;
    filterObject: FilterObjectType;
}

class MetOceanFilters extends Component<MetOceanFiltersComponentProps, MetOceanFiltersComponentState> {
    constructor(props: MetOceanFiltersComponentProps) {
        super(props);
        this.state = {
            port: props.port,
            filterObject: clone(props.filterObject),
            enviroDisplayOrder: props.port.meta_data.enviroDisplayOrder,
            enviroDisplay: props.port.meta_data.enviroDisplay,
            environmentalSources: props.port.port_urls

                ?.filter((x) => x.type === 'environmental')
                .map((x) => {
                    const userStart = props.filterObject.date.start_utc.op ?? props.system.date.startDate;
                    const userEnd = props.filterObject.date.end_utc.op ?? props.system.date.today;

                    //@ts-ignore
                    const defaultData = props.defaultData?.[x.environmental_type]?.[`_${x.id}`]?.date || [
                        x.created_at,
                        x.updated_at,
                    ];

                    const sourceStart = props.dateTimeFormat.parseDateTime(defaultData[0]).startOf('day');
                    const sourceEnd =
                        x.status === 'active'
                            ? props.system.date.today // now
                            : props.dateTimeFormat.parseDateTime(defaultData[1]);

                    let status: 'active' | 'inactive' = 'active';
                    const messages: string[] = []; // accumulate messages

                    const overlaps = sourceStart.isBefore(userEnd.date) && sourceEnd.isAfter(userStart.date);

                    if (!overlaps) {
                        status = 'inactive';
                        messages.push(translate('No data available inside the selected date range'));

                        props.setFilterObject(
                            setKey(clone(props.filterObject), `${x.environmental_type}._${x.id}.status`, false)
                        );
                    } else {
                        status = 'active';

                        const startLimited =
                            sourceStart.isAfter(userStart.date) && !sourceStart.isEqual(props.system.date.today.date);

                        const endLimited =
                            sourceEnd.isBefore(userEnd.date) && !sourceEnd.isEqual(props.system.date.today.date);

                        if (startLimited && endLimited) {
                            messages.push(
                                translate(`Data available between`, {
                                    from: sourceStart.formatDateTimeToString('dd MMM yyyy'),
                                    to: sourceEnd.formatDateTimeToString('dd MMM yyyy'),
                                })
                            );
                        } else {
                            if (startLimited) {
                                messages.push(
                                    translate(`Data available from`, {
                                        date: sourceStart.formatDateTimeToString('dd MMM yyyy'),
                                    })
                                );
                            }

                            if (endLimited) {
                                messages.push(
                                    translate(`Data available to`, {
                                        date: sourceEnd.formatDateTimeToString('dd MMM yyyy'),
                                    })
                                );
                            }
                        }
                    }

                    return {
                        ...x,
                        start_utc: sourceStart,
                        end_utc: sourceEnd,
                        status,
                        message: messages.join(' • '),
                    };
                }),
        };
    }

    static getDerivedStateFromProps(props: MetOceanFiltersComponentProps, state: MetOceanFiltersComponentState) {
        if (
            checkDifferent(props.filterObject, state.filterObject) ||
            checkDifferent(
                props.port.port_urls.map((x) => {
                    return { id: x.id, status: x.status };
                }),
                state.environmentalSources.map((x) => {
                    return { id: x.id, status: x.status };
                })
            )
        ) {
            return {
                environmentalSources: props.port.port_urls

                    ?.filter((x) => x.type === 'environmental')
                    .map((x) => {
                        const userStart = props.filterObject.date.start_utc.op ?? props.system.date.startDate;
                        const userEnd = props.filterObject.date.end_utc.op ?? props.system.date.today;

                        //@ts-ignore
                        const defaultData = props.defaultData?.[x.environmental_type]?.[`_${x.id}`]?.date || [
                            x.created_at,
                            x.updated_at,
                        ];

                        const sourceStart = props.dateTimeFormat.parseDateTime(defaultData[0]).startOf('day');
                        const sourceEnd =
                            x.status === 'active'
                                ? props.system.date.today // now
                                : props.dateTimeFormat.parseDateTime(defaultData[1]);

                        let status: 'active' | 'inactive' = 'active';
                        const messages: string[] = []; // accumulate messages

                        const overlaps = sourceStart.isBefore(userEnd.date) && sourceEnd.isAfter(userStart.date);

                        if (!overlaps) {
                            status = 'inactive';
                            messages.push(translate('No data available inside the selected date range'));

                            props.setFilterObject(
                                setKey(clone(props.filterObject), `${x.environmental_type}._${x.id}.status`, false)
                            );
                        } else {
                            status = 'active';

                            const startLimited =
                                sourceStart.isAfter(userStart.date) &&
                                !sourceStart.isEqual(props.system.date.today.date);

                            const endLimited =
                                sourceEnd.isBefore(userEnd.date) && !sourceEnd.isEqual(props.system.date.today.date);

                            if (startLimited && endLimited) {
                                messages.push(
                                    translate(`Data available between`, {
                                        from: sourceStart.formatDateTimeToString('dd MMM yyyy'),
                                        to: sourceEnd.formatDateTimeToString('dd MMM yyyy'),
                                    })
                                );
                            } else {
                                if (startLimited) {
                                    messages.push(
                                        translate(`Data available from`, {
                                            date: sourceStart.formatDateTimeToString('dd MMM yyyy'),
                                        })
                                    );
                                }

                                if (endLimited) {
                                    messages.push(
                                        translate(`Data available to`, {
                                            date: sourceEnd.formatDateTimeToString('dd MMM yyyy'),
                                        })
                                    );
                                }
                            }
                        }

                        return {
                            ...x,
                            start_utc: sourceStart,
                            end_utc: sourceEnd,
                            status,
                            message: messages.join(' • '),
                        };
                    }),
                filterObject: clone(props.filterObject),
            };
        }

        return null;
    }

    getDisplay = (envirotype, key) => {
        return this.state?.enviroDisplay?.[envirotype][key];
    };

    handelChange = (name: string, value: FilterObjectType) => {
        //@ts-ignore
        this.props.setFilterObject(setKey(clone(this.state.filterObject), name, value));
    };

    handleSliderChange = (names: string[], values: number[]): void => {
        const filterObject = clone(this.state.filterObject); // Clone the filterObject

        names.forEach((name, index) => {
            const value = values[index];
            const defaultValue = getKey(this.props.defaultData, name);

            // Set the current key's value
            setKey(filterObject, name, value);

            // Handle "min" and "max" flags for "gte" and "lte"
            if (name.includes('gte')) {
                const minKey = name.replace('gte', 'min');
                setKey(filterObject, minKey, value === defaultValue);
            } else if (name.includes('lte')) {
                const maxKey = name.replace('lte', 'max');
                setKey(filterObject, maxKey, value === defaultValue);
            }
        });

        this.props.setFilterObject(filterObject); // Update the filterObject
    };

    render() {
        return (
            <SearchGroup open title={translate('MetOcean')}>
                <div className="ml-1">
                    {this.state.enviroDisplayOrder?.map((enviroDisplayOrder: EnviroDisplayOrderType) => {
                        return (
                            <div
                                className=" flex flex-col  border-b border-gray-300 mb-1 pb-1"
                                key={enviroDisplayOrder.key}
                            >
                                <div className="font-bold pb-1">{translate(enviroDisplayOrder.key)}</div>
                                {this.state.environmentalSources
                                    ?.filter((environmentalSource: PortURLType) => {
                                        return environmentalSource.environmental_type === enviroDisplayOrder.key;
                                    })
                                    .map((environmentalSource: PortURLType, index, arrayData) => {
                                        return (
                                            <div key={environmentalSource.id}>
                                                <div className="flex justify-between">
                                                    <div
                                                        className={twMerge(
                                                            `flex`,
                                                            `justify-between`,
                                                            getKey(
                                                                this.state.filterObject,
                                                                `${enviroDisplayOrder.key}._${environmentalSource.id}.status`
                                                            ) && 'font-bold'
                                                        )}
                                                    >
                                                        {environmentalSource.description}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {environmentalSource.message !== '' && (
                                                            <>
                                                                <Tooltip
                                                                    className="z-100"
                                                                    id={`environmentalSource${environmentalSource.id}`}
                                                                    html={environmentalSource.message}
                                                                    place={'bottom-start'}
                                                                    positionStrategy="fixed"
                                                                />
                                                                <div
                                                                    data-tooltip-id={`environmentalSource${environmentalSource.id}`}
                                                                >
                                                                    <Icon className="w-5 h-5 cursor-pointer text-red-500">
                                                                        <path
                                                                            stroke="none"
                                                                            d="M0 0h24v24H0z"
                                                                            fill="none"
                                                                        ></path>
                                                                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                                                                        <path d="M12 9h.01"></path>
                                                                        <path d="M11 12h1v4h1"></path>
                                                                    </Icon>
                                                                </div>
                                                            </>
                                                        )}
                                                        <ToggleButton
                                                            disabled={environmentalSource.status === 'inactive'}
                                                            onChange={this.handelChange}
                                                            name={`${enviroDisplayOrder.key}._${environmentalSource.id}.status`}
                                                            value={getKey(
                                                                this.state.filterObject,
                                                                `${enviroDisplayOrder.key}._${environmentalSource.id}.status`
                                                            )}
                                                        ></ToggleButton>
                                                    </div>
                                                </div>

                                                {getKey(
                                                    this.state.filterObject,
                                                    `${enviroDisplayOrder.key}._${environmentalSource.id}.status`
                                                ) && (
                                                    <>
                                                        <div>
                                                            {enviroDisplayOrder.list.map((x) => {
                                                                return (
                                                                    <div key={`${environmentalSource.id}-${x}`}>
                                                                        {this.getDisplay(enviroDisplayOrder.key, x)
                                                                            .filter_type === 'rangeSlider' && (
                                                                            <RangeSlider
                                                                                onChange={(name, values) => {
                                                                                    this.handleSliderChange(
                                                                                        name,
                                                                                        values.map((y) => {
                                                                                            return (
                                                                                                y /
                                                                                                this.getDisplay(
                                                                                                    enviroDisplayOrder.key,
                                                                                                    x
                                                                                                )
                                                                                                    .database2display_unit_factor
                                                                                            );
                                                                                        })
                                                                                    );
                                                                                }}
                                                                                key={`${enviroDisplayOrder.key}._${environmentalSource.id}.${x}`}
                                                                                name={[
                                                                                    `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.gte`,
                                                                                    `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.lte`,
                                                                                ]}
                                                                                unit={
                                                                                    this.getDisplay(
                                                                                        enviroDisplayOrder.key,
                                                                                        x
                                                                                    ).unit
                                                                                }
                                                                                label={
                                                                                    this.getDisplay(
                                                                                        enviroDisplayOrder.key,
                                                                                        x
                                                                                    ).name
                                                                                }
                                                                                decimal={
                                                                                    this.getDisplay(
                                                                                        enviroDisplayOrder.key,
                                                                                        x
                                                                                    ).decimal
                                                                                }
                                                                                value={[
                                                                                    getKey(
                                                                                        this.state.filterObject,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.gte`
                                                                                    ) *
                                                                                        this.getDisplay(
                                                                                            enviroDisplayOrder.key,
                                                                                            x
                                                                                        ).database2display_unit_factor,
                                                                                    getKey(
                                                                                        this.state.filterObject,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.lte`
                                                                                    ) *
                                                                                        this.getDisplay(
                                                                                            enviroDisplayOrder.key,
                                                                                            x
                                                                                        ).database2display_unit_factor,
                                                                                ]}
                                                                                range={[
                                                                                    getKey(
                                                                                        this.props.defaultData,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.gte`
                                                                                    ) *
                                                                                        this.getDisplay(
                                                                                            enviroDisplayOrder.key,
                                                                                            x
                                                                                        ).database2display_unit_factor,
                                                                                    getKey(
                                                                                        this.props.defaultData,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}.lte`
                                                                                    ) *
                                                                                        this.getDisplay(
                                                                                            enviroDisplayOrder.key,
                                                                                            x
                                                                                        ).database2display_unit_factor,
                                                                                ]}
                                                                            ></RangeSlider>
                                                                        )}

                                                                        {this.getDisplay(enviroDisplayOrder.key, x)
                                                                            .filter_type === 'pieSelect' && (
                                                                            <DirectionSelect
                                                                                onChange={this.handelChange}
                                                                                label={
                                                                                    this.getDisplay(
                                                                                        enviroDisplayOrder.key,
                                                                                        x
                                                                                    ).name
                                                                                }
                                                                                key={`${enviroDisplayOrder.key}._${environmentalSource.id}.${x}`}
                                                                                name={`${enviroDisplayOrder.key}._${environmentalSource.id}.${x}`}
                                                                                value={
                                                                                    getKey(
                                                                                        this.state.filterObject,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}`
                                                                                    ) || []
                                                                                }
                                                                                range={
                                                                                    getKey(
                                                                                        this.props.defaultData,
                                                                                        `${enviroDisplayOrder.key}._${environmentalSource.id}.${x}`
                                                                                    ) || []
                                                                                }
                                                                            ></DirectionSelect>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        {index < arrayData.length - 1 && (
                                                            <div className="flex items-center gap-2 text-sm leading-none text-gray-500">
                                                                <span className="flex-1 border-t border-gray-300"></span>
                                                                OR
                                                                <span className="flex-1 border-t border-gray-300"></span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })}
                </div>
            </SearchGroup>
        );
    }
}

const metOceanFiltersStateToProps = (state: RootState) => ({
    port: state.port.port,
    defaultData: state.port.defaultData,
    dateTimeFormat: state.port.dateTimeFormat,
    system: state.port.system,
});

const metOceanFiltersDispatchToProps = () => ({});

export default connect(metOceanFiltersStateToProps, metOceanFiltersDispatchToProps)(MetOceanFilters);
